import { useEffect, useMemo, useState } from "react";
import { readViewAs, type ViewAsState } from "@util/viewAs";

export interface ViewAs extends ViewAsState {
    /** The two hours are up: the screen may still be on show, but every call will 401. */
    ended: boolean;
}

/**
 * Whether this tab is staff looking at the merchant's screens (#4112).
 *
 * Read from the session token, so it needs no provider and is right on the
 * first render: a write control can render `disabled={viewAs.active}` without a
 * flash of an enabled button. `ended` flips on the token's own clock, so the
 * banner can say the view is over before the first 401 does.
 *
 * Use this to hide or disable a control that writes. The server still refuses
 * the write (403 `view_as_read_only`) whatever the screen shows.
 */
export function useViewAs(): ViewAs {
    const state = useMemo(() => readViewAs(), []);
    const [ended, setEnded] = useState(() => state.expiresAt !== null && state.expiresAt <= Date.now());

    useEffect(() => {
        if (!state.active || state.expiresAt === null || ended) return;
        // setTimeout caps at ~24.8 days; a two-hour session is nowhere near it.
        const timer = window.setTimeout(() => setEnded(true), Math.max(0, state.expiresAt - Date.now()));
        return () => window.clearTimeout(timer);
    }, [state, ended]);

    return { ...state, ended: state.active && ended };
}
